import { useMemo } from 'react';
import type { Board } from '../lib/types';
import { distance, hours, nowSecondsIn } from '../lib/format';

/**
 * The morning-to-evening summary a dispatcher glances at every few minutes.
 *
 * Ordered by what they act on: how much work is done, what is running late,
 * what is about to miss a promise, and what nobody is going to reach at all.
 *
 * The day's planned totals -- cost, orders, vehicles, distance -- are not here.
 * They live in the dispatch sidebar, which is where the incumbent puts them,
 * and duplicating them would put the same six numbers on screen twice.
 * Violations is the exception and belongs here: it is a count of things
 * currently wrong, which is the same question as the rest of this strip.
 */
export function KpiStrip({ board }: { board: Board | null }) {
  const kpi = useMemo(() => {
    if (!board) {
      return {
        stops: 0, completed: 0, active: 0, notStarted: 0, late: 0, atRisk: 0,
        unassigned: 0, distance: 0, drive: 0, onTimeRate: null as number | null,
        violations: 0,
      };
    }

    const stops = board.routes.flatMap((r) => r.stops);
    const done = stops.filter((s) => s.status === 'completed' || s.status === 'failed');
    const onTime = done.filter((s) => (s.deltaSeconds ?? 0) < 900);

    /*
     * A route that has not left the depot is a different problem from one that
     * is running behind, even though both drift against plan. Counting them
     * together produced "3 routes late, 0 technicians out", which tells a
     * dispatcher nothing about what to do next.
     */
    const started = board.routes.filter((r) => r.actualStart !== null);
    const nowSeconds = nowSecondsIn(board.territory.timezone);
    const overdueStart = board.routes.filter(
      /* Only a route with work on it can be late to start; an empty or switched-off lane has nowhere to be. */
      (r) => r.stops.length > 0 && r.actualStart === null && r.plannedStart <= nowSeconds - 600,
    );

    return {
      stops: stops.length,
      completed: done.length,
      active: started.filter((r) => r.actualEnd === null).length,
      notStarted: overdueStart.length,
      late: started.filter((r) => r.deltaSeconds >= 900).length,
      atRisk: stops.filter((s) => s.status === 'pending' && s.windowRisk === 'late').length,
      unassigned: board.unassigned.length,
      distance: board.routes.reduce((n, r) => n + r.plannedDistanceM, 0),
      drive: board.routes.reduce((n, r) => n + r.plannedDriveS, 0),
      violations: board.routes.reduce((n, r) => n + (r.violationCounts?.total ?? 0), 0),
      onTimeRate: done.length > 0 ? Math.round((onTime.length / done.length) * 100) : null,
    };
  }, [board]);

  const progress = kpi.stops > 0 ? Math.round((kpi.completed / kpi.stops) * 100) : 0;

  return (
    <div className="kpis">
      <Cell label="Progress" value={`${progress}%`} hint={`${kpi.completed} of ${kpi.stops}`} />
      <Cell label="Technicians out" value={String(kpi.active)} />
      <Cell
        label="Yet to depart"
        value={String(kpi.notStarted)}
        tone={kpi.notStarted === 0 ? undefined : 'warn'}
      />
      <Cell
        label="On time"
        value={kpi.onTimeRate === null ? '--' : `${kpi.onTimeRate}%`}
        tone={kpi.onTimeRate === null ? undefined : kpi.onTimeRate >= 90 ? 'ok' : kpi.onTimeRate >= 75 ? 'warn' : 'late'}
      />
      <Cell
        label="Routes late"
        value={String(kpi.late)}
        tone={kpi.late === 0 ? undefined : kpi.late > 3 ? 'late' : 'warn'}
      />
      <Cell
        label="Windows at risk"
        value={String(kpi.atRisk)}
        tone={kpi.atRisk === 0 ? undefined : 'late'}
      />
      <Cell
        label="Unassigned"
        value={String(kpi.unassigned)}
        tone={kpi.unassigned === 0 ? undefined : 'warn'}
      />
      <Cell
        label="Violations"
        value={String(kpi.violations)}
        tone={kpi.violations === 0 ? undefined : 'warn'}
      />
      <Cell label="Planned distance" value={distance(kpi.distance, board?.territory.distanceUnit)} />
      <Cell label="Drive time" value={hours(kpi.drive)} />
    </div>
  );
}

function Cell({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'ok' | 'warn' | 'late';
}) {
  return (
    <div className="kpi" data-tone={tone}>
      <b>{value}</b>
      <span>{hint ? `${label} · ${hint}` : label}</span>
    </div>
  );
}
